/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useParams } from "react-router-dom";
import { BrickLoader } from "@nordcloud/gnui";
import {
  UniversalWrap,
  CostTableData,
  getColorsPalette,
  getTableColumns,
  StackBarChart,
  generateLinkParameters,
  getEndDate,
  mapStackBarChartColors,
  ChartType,
  Granularity,
  Period,
  LinkQueryParameterName,
} from "~/components";
import { useCurrency } from "~/hooks";
import { RouteParams } from "~/views/environments/types";
import { useEnvCostPerCategoriesChart } from "../hooks/useEnvCostPerCategoriesChart";

type Props = {
  environmentId: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
};

export function EnvCostPerCategoryView({
  environmentId,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();
  const { environment = "" } = useParams<RouteParams>();

  const {
    environmentTopCategoriesCostChartData,
    costPerCategoryTableData,
    costPerCategoryTotal,
    loading,
    error,
    getSubRows,
    environments,
    otherCategoryIds,
  } = useEnvCostPerCategoriesChart({
    environmentId,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const linkParameters = useMemo(
    () =>
      generateLinkParameters(
        LinkQueryParameterName.ENVIRONMENT,
        [environment],
        otherCategoryIds
      ),
    [environment, otherCategoryIds]
  );

  const tableColumns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: costPerCategoryTableData,
        hasFooter,
        period,
        chartType: ChartType.COST_PER_CATEGORY,
        colorsPalette: getColorsPalette(ChartType.COST_PER_CATEGORY),
        linkParameters,
      }),
    [
      startDate,
      endDate,
      currency,
      hasFooter,
      costPerCategoryTableData,
      linkParameters,
      period,
    ]
  );

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <ParentSize>
        {(parent) => (
          <StackBarChart
            width={parent.width}
            height={280}
            data={environmentTopCategoriesCostChartData}
            granularity={granularity}
            loading={loading}
            startDate={startDate}
            endDate={endDate}
            period={period}
            colors={mapStackBarChartColors(environments)}
            keys={environments}
          />
        )}
      </ParentSize>
      <CostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={costPerCategoryTableData}
        chartType={ChartType.COST_PER_CATEGORY}
        total={costPerCategoryTotal}
        tableError={error}
        columns={tableColumns}
        isLoading={loading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
        getSubRows={getSubRows}
      />
    </UniversalWrap>
  );
}
